import { useEffect, useMemo, useRef, useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, ArrowLeft, ArrowRight, Check, Delete, Music, Search, UserPlus } from 'lucide-react'
import {
  addMakeupVisitor,
  clockTime,
  dayLabel,
  deviceToken,
  fetchDeviceContext,
  fetchMusicFolders,
  fetchSheet,
  fetchUnrepairedAbsences,
  filterMusic,
  lookupDancer,
  markAttendance,
  meridiem,
  needsOverride,
  progress,
  removeMakeupVisitor,
  reportedSummary,
  routineLabel,
  sessionMarkers,
  setDeviceToken,
  unlock,
  NOT_YOURS,
  SESSION_ENDED,
  type AttendanceState,
  type SessionMarker,
  type SessionOption,
  type SheetDancer,
} from '../api/attendance'
import TakeOffTheNight from './TakeOffTheNight'
import { minutesNow } from '../api/dancers'
import { addDays } from '../api/calendar'
import { describeClass } from '../api/classDescription'
import { roomDay, type RoomDay } from '../api/roomDay'
import { fetchContent, has, text } from '../api/content'
import BuildCredit from './BuildCredit'
import { MarkButtons } from './MarkButtons'

/**
 * The tablet in the studio room (D-116, D-119).
 *
 * **Designed for fifteen seconds between two classes.** The device knows which
 * room it is in, so the class in front of the teacher is already on screen; a
 * PIN opens the roster; every dancer is one tap. There is no submit button,
 * because a register left unsent is worse than no register at all — each tap
 * saves on its own.
 *
 * **Music sits in front of the lock.** Dancers use empty rooms during breaks to
 * run routines, and a password between a fourteen-year-old and her own music is
 * a feature nobody would use (D-119).
 *
 * ⚠ **The unlocked screen does name a child, on a solo, duet or trio, and that
 * is deliberate** (D-915). This comment claimed the opposite until then and was
 * already false when it was written — the Jackrabbit name itself carries the
 * first name on 87 of 190 classes. A **first name** is the bound the owner set;
 * a surname or a roster is not, and the roster still needs a PIN.
 */
export default function ClassroomView() {
  return (
    /*
     * The credit is attached to the route, not to a screen (D-228, amended by
     * the 2026-08-20 room spec).
     *
     * This screen is seven screens behind one URL — setup, waking, unreachable,
     * the day's list, the PIN pad, the roster, the music browser — each an early
     * return. Putting the credit inside any of them would mean putting it inside
     * all of them, and the eighth screen somebody adds next year would quietly
     * be the one without it. Here it cannot be missed and cannot be doubled.
     *
     * `mt-auto` on the quiet tone does the placing: the column is at least the
     * viewport, the screen above it takes `flex-1`, so the credit lands at the
     * bottom and never between a teacher and a register.
     */
    <div className="flex min-h-full flex-col">
      <RoomScreens />
      <BuildCredit tone="quiet" />
    </div>
  )
}

function RoomScreens() {
  const [pinFor, setPinFor] = useState<{ occurrenceId: string; className: string } | null>(null)
  const [openSheet, setOpenSheet] = useState<string | null>(null)
  const [showMusic, setShowMusic] = useState(false)

  /*
   * Which of the three buckets is open, or none. One at a time: three lists at
   * once is the wall of rows this screen exists to stop being.
   */
  const [open, setOpen] = useState<Bucket | null>(null)

  /*
   * Find-a-dancer: asked for by the owner, and behind the PIN (D-249).
   *
   * `'locked'` while the PIN is being entered, `'open'` once it has been. Two
   * states rather than one boolean because the lock is the whole point — a box
   * that answers "is this named child in the building, and which room" is
   * exactly what D-119 was written against, and it sits on a screen in a public
   * corridor with no sign-out.
   */
  const [find, setFind] = useState<'closed' | 'locked' | 'open'>('closed')

  /*
   * The day on screen, or null for "whatever the studio calls today" (D-235).
   *
   * Null rather than an ISO date on purpose. The tablet is left on overnight, so
   * a screen that resolved "today" once at load and held it would still be
   * showing Tuesday on Wednesday evening — the refresh has to be able to move
   * the day, and it only can if nobody has pinned one.
   */
  const [chosenDate, setChosenDate] = useState<string | null>(null)

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['device', chosenDate],
    queryFn: () => fetchDeviceContext(chosenDate ?? undefined),
    retry: false,
    // A wall-mounted screen is left on all evening. Without this it shows the
    // 4:30 class at 8pm.
    refetchInterval: 60_000,

    /*
     * Keep the day on screen while the next one loads.
     *
     * The date is part of the query key, so without this every tap of a day
     * arrow drops the whole screen — header, arrows and all — to "Waking up…"
     * until the request lands, and the control the teacher just pressed is not
     * there to press again. Found by a test rather than by reading: two taps in
     * a row failed because the second one had nothing to hit.
     */
    placeholderData: keepPreviousData,
  })

  /*
   * Re-rendered on a timer so the on-now marker moves without a reload.
   *
   * The 60-second device refetch would eventually do it, but the marker is read
   * at a glance between two classes and a card that says "on now" for a class
   * that ended is worse than no marker. Thirty seconds, and cheap: it changes a
   * number, not a request.
   */
  const [tick, setTick] = useState(() => minutesNow())

  useEffect(() => {
    const id = setInterval(() => setTick(minutesNow()), 30_000)
    return () => clearInterval(id)
  }, [])

  /*
   * The logo, and what happens when it does not arrive (D-209, D-33).
   *
   * Its own request, deliberately not joined to the device one: a database-backed
   * endpoint refuses for ~75 seconds after a deploy, and the register is the
   * thing this screen is for. A missing logo is a missing logo — it must never
   * hold up, or make a claim about, the studio's schedule. `has()` is false for
   * an undefined map, so a failed request renders no image rather than a broken
   * one.
   */
  const { data: content } = useQuery({ queryKey: ['content'], queryFn: fetchContent })

  if (isPending) return <Splash>Waking up&hellip;</Splash>

  if (isError) {
    return (error as Error).message === 'UNREGISTERED' ? (
      <Register onDone={() => refetch()} />
    ) : (
      <Splash>
        Could not reach the studio.
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-6 rounded-xl bg-accent px-6 py-3 text-[16px] font-semibold text-on-accent"
        >
          Try again
        </button>
      </Splash>
    )
  }

  if (openSheet) {
    return (
      <Roster
        occurrenceId={openSheet}
        onClose={() => setOpenSheet(null)}
        /*
         * Straight back to the PIN for the class she was already in — not to the
         * list of classes. She is standing in front of the dancers with the
         * register half done; making her find the class again is a step with no
         * decision in it, and the tap that failed still has to be redone.
         */
        onSessionEnded={() => {
          const session = [...data.now, ...data.elsewhere].find(
            (s) => s.occurrenceId === openSheet,
          )

          setOpenSheet(null)
          setPinFor({
            occurrenceId: openSheet,
            className: session?.className ?? '',
          })
        }}
      />
    )
  }

  if (showMusic) return <MusicBrowser onClose={() => setShowMusic(false)} />

  if (find === 'locked') {
    return (
      <PinPad
        className=""
        prompt="Find a dancer"
        teachers={data.teachers}
        onCancel={() => setFind('closed')}
        onUnlocked={() => setFind('open')}
      />
    )
  }

  if (find === 'open') return <FindDancer onClose={() => setFind('closed')} />

  if (pinFor) {
    return (
      <PinPad
        className={pinFor.className}
        teachers={data.teachers}
        onCancel={() => setPinFor(null)}
        onUnlocked={() => {
          setOpenSheet(pinFor.occurrenceId)
          setPinFor(null)
        }}
      />
    )
  }

  const here = data.now
  const elsewhere = data.elsewhere

  const isToday = data.date === data.today
  const markers = sessionMarkers([...here, ...elsewhere], tick, isToday)

  /*
   * **`hasRoom` reads the room name, not the id**, because that is what the
   * payload carries — and a device with no room is the D-116 escape hatch
   * rather than a misconfiguration: a dead tablet gets replaced by whichever
   * one is free, and a hard binding would block a teacher on a class night.
   */
  const day = roomDay(here, tick, { isToday, hasRoom: data.roomName !== null })

  /*
   * **The occurrence is taken from the press, not from the render** (D-914).
   *
   * ⚠ The screen re-renders every thirty seconds and *on now* is half-open
   * (D-81), so at 7:30:00 exactly the hero changes identity. In the Purple Room
   * that is eleven consecutive thirty-minute slots, often the same teacher and
   * the same label — so between her deciding and her finger landing, the button
   * can come to mean the next class. The register she opens would be the wrong
   * one, and attendance is the thing Jackrabbit cannot regenerate.
   */
  const take = (session: SessionOption) =>
    setPinFor({ occurrenceId: session.occurrenceId, className: session.className })

  /*
   * Day arrows move from what was *asked for*, not from what has arrived.
   *
   * `data.date` is the server's echo of the last request that landed, so two
   * quick taps computed from it both resolve to the same day+1 and the second
   * tap does nothing — which on a tablet reads as a dropped press, and the
   * teacher presses harder. `chosenDate` is the intent and is already correct
   * before the request returns. The heading still follows `data.date`, so it
   * never shows a day above a list belonging to another one.
   */
  const showing = chosenDate ?? data.today

  const goto = (iso: string) => {
    // Back to null when it lands on today, so the screen resumes following the
    // studio's clock instead of being pinned to a date that will go stale.
    setChosenDate(iso === data.today ? null : iso)
  }

  return (
    <div className="mx-auto w-full max-w-[46rem] flex-1 px-5 py-6">
      <header className="mb-5 flex items-center gap-3">
        {/*
          The studio's own logo, on the one screen everyone in the building sees.

          Black and pink on a white ground with no transparency, so it needs the
          white tile — without it the mark renders as a white box for anyone whose
          tablet is on the dark theme. Same constraint as the public header, and
          the same fallback: no block published, no element at all, because an
          empty `src` is a broken-image icon on a wall.

          **Capped in width, not just in height.** Tonya uploads this file and can
          replace it whenever she likes, so its aspect ratio is not something this
          screen gets to assume. Height alone plus `w-auto` lets a wide mark set
          the header's width and push the whole page sideways at 390px, which is
          the exact shape of the bug the browser sweep found before (D-229).
          `max-w` bounds it and `object-contain` keeps whatever she picks
          undistorted inside that box.
        */}
        {has(content, 'site.logo') && (
          <img
            src={text(content, 'site.logo')}
            alt="Cabot Dance Academy"
            className="h-11 w-auto max-w-[8rem] shrink-0 rounded-lg bg-white object-contain px-1.5 py-1"
          />
        )}

        {/* The name and date keep their shared baseline; the logo sits beside
            the pair rather than inside their alignment. */}
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
          <h1 className="font-display text-2xl">{data.roomName ?? data.deviceName}</h1>
          <p className="text-[13.5px] text-ink-3">{dayLabel(data.date)}</p>
        </div>

        <div className="ml-auto flex items-center gap-2">
          {/*
            Beside the music, where the owner asked for it, and locked (D-249).
            Music is deliberately in front of the lock — a password between a
            fourteen-year-old and her own routine is a feature nobody uses
            (D-119) — and this is deliberately behind it, for the same entry's
            other half: a dancer's whereabouts is not a thing a corridor screen
            answers to whoever walks up.
          */}
          <button
            type="button"
            onClick={() => setFind('locked')}
            className="flex items-center gap-2 rounded-xl border border-line-2 px-4 py-2.5 text-[14px] font-semibold text-ink-2"
          >
            <Search size={16} strokeWidth={2} />
            Find a dancer
          </button>

          <button
            type="button"
            onClick={() => setShowMusic(true)}
            className="flex items-center gap-2 rounded-xl border border-line-2 px-4 py-2.5 text-[14px] font-semibold text-ink-2"
          >
            <Music size={16} strokeWidth={2} />
            Music
          </button>
        </div>
      </header>

      {/*
        Moving off today (D-235).

        Tap targets are deliberately large: this is operated with a thumb, on a
        wall, by somebody holding a folder. The Today control appears only when
        it would do something — a button that is its own no-op teaches people to
        distrust the row.
      */}
      <div className="mb-5 flex items-center gap-2">
        <button
          type="button"
          onClick={() => goto(addDays(showing, -1))}
          aria-label="Previous day"
          className="grid size-11 place-items-center rounded-xl border border-line-2 text-ink-2"
        >
          <ArrowLeft size={18} strokeWidth={2} />
        </button>

        <button
          type="button"
          onClick={() => goto(addDays(showing, 1))}
          aria-label="Next day"
          className="grid size-11 place-items-center rounded-xl border border-line-2 text-ink-2"
        >
          <ArrowRight size={18} strokeWidth={2} />
        </button>

        {!isToday && (
          <button
            type="button"
            onClick={() => goto(data.today)}
            className="rounded-xl border border-line-2 px-4 py-2.5 text-[14px] font-semibold text-ink-2"
          >
            Today
          </button>
        )}
      </div>

      {here.length === 0 && elsewhere.length === 0 && (
        <p className="rounded-2xl border border-line bg-surface px-5 py-8 text-center text-[15px] text-ink-3">
          {/* Names the day rather than saying "today" — after D-235 the screen
              is often not showing today, and the old copy said so regardless. */}
          Nothing scheduled in the studio on {dayLabel(data.date)}.
        </p>
      )}

      {/*
        **The class in front of her takes the screen** (D-914). Nine equal rows
        is what the owner called overwhelming; one card is the answer nine times
        out of ten, and the rest of the room is two buttons.
      */}
      {(day.hero !== null || day.dayIsOver) && (
        <>
          {day.hero !== null ? (
            <>
              <HeroCard session={day.hero} is={day.heroIs!} onTake={take} />

              {day.next !== null && <NextCard session={day.next} onTake={take} />}
            </>
          ) : (
            /*
              **The evening is over, and the three buttons stay** (D-914).

              ⚠ For one commit this case fell through to the full nine-row list
              — the screen the owner called overwhelming — and took the untaken
              count off the screen with it. That is backwards: *"a teacher
              trying to go fill in something they forgot"* is a teacher doing it
              **after** her classes, so this is the state his request is most
              about, not an afterthought. `roomDay` had always counted it
              correctly; nothing surfaced it. Found by an audit.
            */
            <section className="mb-3 rounded-2xl border border-line bg-surface px-5 py-7 text-center">
              <p className="text-[17px] text-ink-2">That&rsquo;s everything for today.</p>
              {/* The studio says *attendance*; only enrolment says register
                  (D-378), and `src/registerWording.test.ts` is what catches it. */}
              <p className="mt-1 text-[13.5px] text-ink-3">
                {day.untakenEarlier.length === 0
                  ? 'Attendance was taken for every class.'
                  : `${day.untakenEarlier.length} ${day.untakenEarlier.length === 1 ? 'class' : 'classes'} never had attendance taken.`}
              </p>
            </section>
          )}

          <div className="mb-6 flex flex-col gap-2.5 sm:flex-row">
            <BucketButton
              label="Earlier today in this room"
              count={day.earlierHere.length}
              note={outstanding(day)}
              open={open === 'earlier'}
              onToggle={() => setOpen(open === 'earlier' ? null : 'earlier')}
            />
            <BucketButton
              label="Later today in this room"
              count={day.laterHere.length}
              open={open === 'later'}
              onToggle={() => setOpen(open === 'later' ? null : 'later')}
            />
            <BucketButton
              label="Elsewhere in the studio"
              count={elsewhere.length}
              open={open === 'elsewhere'}
              onToggle={() => setOpen(open === 'elsewhere' ? null : 'elsewhere')}
            />
          </div>

          {open !== null && (
            <Section label={OPEN_LABELS[open]}>
              {bucket(day, elsewhere, open).map((session) => (
                <SessionCard
                  key={session.occurrenceId}
                  session={session}
                  marker={markers.get(session.occurrenceId) ?? null}
                  showRoom={open === 'elsewhere'}
                  notTaken={day.untakenEarlier.some((s) => s.occurrenceId === session.occurrenceId)}
                  onTake={() => take(session)}
                />
              ))}

              {bucket(day, elsewhere, open).length === 0 && (
                <p className="rounded-2xl border border-line bg-surface px-5 py-6 text-center text-[15px] text-ink-3">
                  Nothing {open === 'earlier' ? 'earlier' : open === 'later' ? 'later' : 'elsewhere'} today.
                </p>
              )}
            </Section>
          )}
        </>
      )}

      {/*
        **The fallback, and it is not a lesser screen** — it is what a spare
        tablet in no room shows (D-116), and what any tablet shows on a day that
        is not today (D-235). A hero would be claiming a room it is not in, or
        calling a Thursday class "on now" on a Tuesday.

        **And it is these two cases only, never the day being over** — the
        buttons say *today* and *in this room*, so they are honest about a
        finished evening and would be lies about the other two.
      */}
      {day.hero === null && !day.dayIsOver && here.length > 0 && (
        <Section label="In this room">
          {here.map((session) => (
            <SessionCard
              key={session.occurrenceId}
              session={session}
              marker={markers.get(session.occurrenceId) ?? null}
              showRoom={false}
              notTaken={day.untakenEarlier.some((s) => s.occurrenceId === session.occurrenceId)}
              onTake={() => take(session)}
            />
          ))}
        </Section>
      )}

      {/*
        The escape hatch (D-116). A dead tablet gets replaced by whichever one
        is free, and a hard room binding would block a teacher on a class night
        — which is the friction this whole screen exists to remove.
      */}
      {day.hero === null && !day.dayIsOver && elsewhere.length > 0 && (
        <Section label={here.length > 0 ? 'Elsewhere in the studio' : dayLabel(data.date)}>
          {elsewhere.map((session) => (
            <SessionCard
              key={session.occurrenceId}
              session={session}
              marker={markers.get(session.occurrenceId) ?? null}
              showRoom
              onTake={() => take(session)}
            />
          ))}
        </Section>
      )}
    </div>
  )
}

function Splash({ children }: { children: React.ReactNode }) {
  return (
    // `flex-1`, not `min-h-full`: inside the route's column a child claiming the
    // whole viewport pushes the credit below the fold instead of sitting above it.
    <div className="grid flex-1 place-items-center px-6 text-center">
      <div className="text-[17px] text-ink-2">{children}</div>
    </div>
  )
}

type Bucket = 'earlier' | 'later' | 'elsewhere'

const OPEN_LABELS: Record<Bucket, string> = {
  earlier: 'Earlier today in this room',
  later: 'Later today in this room',
  elsewhere: 'Elsewhere in the studio',
}

function bucket(day: RoomDay, elsewhere: readonly SessionOption[], which: Bucket): SessionOption[] {
  if (which === 'earlier') return day.earlierHere
  if (which === 'later') return day.laterHere

  return [...elsewhere]
}

/**
 * *"6, 2 not taken"* — or *"6, all taken"*, which is the half that lets her
 * stop looking.
 *
 * `asked`: *"showing all of the ones from earlier with the ones that didn't get
 * attendance taken might make the most sense to a teacher trying to go fill in
 * something they forgot."* So the button names what is **outstanding**, not how
 * many classes there were, and the at-a-glance answer the old nine-row list
 * gave survives being collapsed behind a tap.
 */
function outstanding(day: RoomDay): string | null {
  if (day.earlierHere.length === 0) return null

  return day.untakenEarlier.length === 0
    ? 'all taken'
    : `${day.untakenEarlier.length} not taken`
}

/**
 * The class in front of her (D-914, D-915).
 *
 * **The teacher's name is the largest thing on the screen.** `asked`: *"its
 * more important that its the teachers name that is shown in larger text
 * anyways"* — because *"is this mine?"* is the question that decides whether
 * she reads any further, and it is read from across the room.
 */
function HeroCard({
  session,
  is,
  onTake,
}: {
  session: SessionOption
  is: 'now' | 'next'
  onTake: (session: SessionOption) => void
}) {
  const described = describeClass(session.className, session.routineName)
  const held = session.enrolled === 0

  return (
    <section
      className={`mb-3 rounded-2xl border-2 bg-surface px-5 py-5 sm:px-7 sm:py-6 ${
        is === 'now' ? 'border-accent' : 'border-line-2'
      }`}
    >
      <div className="flex flex-wrap items-center gap-x-7 gap-y-4">
        <div className="shrink-0">
          <p
            className={`mb-1 text-[12px] font-bold tracking-[0.11em] uppercase ${
              is === 'now' ? 'text-accent' : 'text-ink-3'
            }`}
          >
            {is === 'now' ? 'On now' : 'Next'}
          </p>

          {session.startTime ? (
            <>
              <p className="text-[38px] leading-none font-semibold tabular">
                {clockTime(session.startTime)}
              </p>
              <p className="mt-1 text-[14px] text-ink-3 tabular">
                to {clockTime(session.endTime ?? session.startTime)}
                {meridiem(session.endTime ?? session.startTime)}
              </p>
            </>
          ) : (
            // A class with no start time meets on no date (D-84), so it is
            // never the hero - but say it rather than leave a gap.
            <p className="text-[16px] text-ink-3">No time</p>
          )}
        </div>

        <div className="min-w-[14rem] flex-1">
          {session.instructors.length > 0 ? (
            <p className="font-display text-[30px] leading-tight wrap-anywhere">
              {session.instructors.join(', ')}
            </p>
          ) : (
            // Ten of the studio's classes carry no instructor at all; a gap
            // where the headline should be reads as a broken screen.
            <p className="font-display text-[30px] leading-tight text-ink-3">Not assigned</p>
          )}

          <p className="mt-1.5 text-[19px] text-ink-2 wrap-anywhere">
            {described.label}
            {described.who !== null && <span className="text-ink-3"> &middot; {described.who}</span>}
            {described.detail !== null && <span className="text-ink-3"> &middot; {described.detail}</span>}
          </p>

          <p className="mt-2 text-[14px] text-ink-3 tabular">
            {held ? (
              // Never "0 of 0 marked", which reads as a job not yet done (D-80).
              <span>Room held &mdash; no dancers yet</span>
            ) : (
              <span>
                {session.enrolled} {session.enrolled === 1 ? 'dancer' : 'dancers'} &middot;{' '}
                {session.marked === 0 ? 'none marked yet' : `${session.marked} marked`}
              </span>
            )}
          </p>
        </div>

        <div className="flex shrink-0 flex-col gap-2.5">
          <TakeButton
            session={session}
            onTake={onTake}
            className={`min-h-14 px-7 text-[18px] ${
              is === 'now' ? 'bg-accent text-on-accent' : 'border border-line-2 bg-surface text-ink'
            }`}
          >
            Take attendance
          </TakeButton>

          {/*
            **This class's own folder, not the browser the header already
            opens** (D-119). A second control called *Music* doing the same
            thing as the header's is a duplicate accessible name on one
            screen — found by `BuildCredit.routes.test.tsx`, which clicks
            Music by name and suddenly had two to choose from. The row this
            hero replaced linked straight to the class's folder, so that is
            what is kept.
          */}
          {session.musicUrl && (
            <a
              href={session.musicUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Music for ${described.label}`}
              className="grid min-h-11 place-items-center rounded-xl border border-line-2 bg-surface px-7 text-[14px] font-semibold text-ink-2 no-underline"
            >
              Music
            </a>
          )}
        </div>
      </div>
    </section>
  )
}

/** The one after the hero: the same shape, smaller, so the eye learns one. */
function NextCard({
  session,
  onTake,
}: {
  session: SessionOption
  onTake: (session: SessionOption) => void
}) {
  const described = describeClass(session.className, session.routineName)

  return (
    <section className="mb-3 rounded-2xl border border-line bg-surface px-5 py-4">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
        <div className="w-[5.5rem] shrink-0">
          <p className="mb-0.5 text-[11px] font-bold tracking-[0.11em] text-ink-3 uppercase">Next</p>
          <p className="text-[22px] leading-none font-semibold tabular">
            {session.startTime ? clockTime(session.startTime) : '--'}
          </p>
        </div>

        <div className="min-w-[12rem] flex-1">
          <p className="font-display text-[20px] leading-tight wrap-anywhere">
            {session.instructors.length > 0 ? session.instructors.join(', ') : 'Not assigned'}
          </p>
          <p className="mt-0.5 text-[15px] text-ink-2 wrap-anywhere">
            {described.label}
            {described.who !== null && <span className="text-ink-3"> &middot; {described.who}</span>}
          </p>
        </div>

        <TakeButton
          session={session}
          onTake={onTake}
          className="ml-auto min-h-11 border border-line-2 bg-surface px-5 text-[15px] text-ink"
        >
          Take
        </TakeButton>
      </div>
    </section>
  )
}

/**
 * The control that opens a register, and the only thing that knows **which**
 * one (D-914, D-81).
 *
 * ⚠ **The occurrence is frozen on `pointerdown`, not read on `click`.** The
 * screen re-ticks every thirty seconds and *on now* is half-open, so at
 * 7:30:00 exactly the promoted cards change identity — and the Purple Room is
 * eleven consecutive thirty-minute slots, often the same teacher and the same
 * label, so nothing on screen tells her the button came to mean another class
 * between her deciding and her finger landing. Attendance is the one thing
 * Jackrabbit cannot regenerate.
 *
 * ⚠ **It is one component because the hero paid for this guard and the next
 * card did not**, for one commit — the same thirty-second window, one card
 * down, found by an audit. Two implementations of one idea drift, and the
 * second never receives the first's fixes.
 *
 * *The rows behind the buckets need no freeze: they are keyed by occurrence,
 * so a tick re-renders a row with the same class rather than a different one.
 * Only a promoted card can change what it means in place.*
 */
function TakeButton({
  session,
  onTake,
  className,
  children,
}: {
  session: SessionOption
  onTake: (session: SessionOption) => void
  className: string
  children: React.ReactNode
}) {
  const pressed = useRef<SessionOption | null>(null)

  return (
    <button
      type="button"
      onPointerDown={() => (pressed.current = session)}
      // A press dragged off the control never fires `click`, so the stale
      // occurrence would otherwise sit in the ref until the next press.
      onPointerCancel={() => (pressed.current = null)}
      onClick={() => {
        const taking = pressed.current ?? session
        pressed.current = null
        onTake(taking)
      }}
      className={`shrink-0 rounded-xl font-semibold ${className}`}
    >
      {children}
    </button>
  )
}

/** One of the three ways to everything that is not the hero. */
function BucketButton({
  label,
  count,
  note,
  open,
  onToggle,
}: {
  label: string
  count: number
  note?: string | null
  open: boolean
  onToggle: () => void
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className={`min-h-15 flex-1 rounded-xl border px-4 py-3 text-left ${
        open ? 'border-ink-3 bg-surface-2' : 'border-line-2 bg-surface'
      }`}
    >
      <span className="block text-[16px] font-semibold text-ink">{label}</span>
      <span className="mt-0.5 block text-[13px] text-ink-3 tabular">
        {count} {count === 1 ? 'class' : 'classes'}
        {note != null && <> &middot; {note}</>}
      </span>
    </button>
  )
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="mb-6">
      <h2 className="mb-2 text-[12px] font-bold tracking-[0.11em] text-ink-3 uppercase">{label}</h2>
      <div className="flex flex-col gap-2.5">{children}</div>
    </section>
  )
}

function SessionCard({
  session,
  notTaken = false,
  marker,
  showRoom,
  onTake,
}: {
  session: SessionOption
  /**
   * Nobody opened this register (D-914).
   *
   * `asked`: *"showing all of the ones from earlier with the ones that didn't
   * get attendance taken might make the most sense to a teacher trying to go
   * fill in something they forgot."*
   *
   * **Badged where it sits rather than sorted to the top**: a teacher hunting
   * the one she forgot is scanning for the badge, and a teacher after *the 5:30*
   * is scanning for the time. Reordering would serve the first and cost the
   * second.
   */
  notTaken?: boolean
  marker: SessionMarker
  /** The room, when the section heading has not already said it. */
  showRoom: boolean
  onTake: () => void
}) {
  const complete = session.enrolled > 0 && session.marked >= session.enrolled

  // A held slot with nobody in it yet (D-80). `SDT SPOT` rows are real: the
  // teacher holds the room and the dancer is assigned later.
  const held = session.enrolled === 0

  return (
    <div
      className={`rounded-2xl border bg-surface px-4 py-3.5 ${
        // The class in front of the teacher, found without reading (D-235).
        marker === 'now' ? 'border-accent ring-1 ring-accent' : 'border-line'
      }`}
    >
      <div className="flex flex-wrap items-start gap-x-4 gap-y-3">
        {/*
          The time, first and loudest (2026-08-20).

          <b>This card used to lead with the Jackrabbit class name.</b> That name
          is machine-shaped — `COMP_SOLO | MCKENNA (SKYE) | TUE_445 | CAITLIN` —
          and it was the only thing set in bold, while the two questions a
          teacher actually arrives with (<em>when</em>, and <em>is it mine</em>)
          sat underneath in grey. On a Tuesday the Purple Room runs eleven
          back-to-back thirty-minute slots, so a wall of near-identical bold
          strings is exactly the screen this one was supposed to replace.

          A fixed rail rather than inline text so the times line up down the
          column and can be scanned without reading any of them.
        */}
        <div className="w-[3.75rem] shrink-0 text-right tabular">
          {session.startTime ? (
            <>
              <p className="text-[17px] leading-tight font-semibold">
                {clockTime(session.startTime)}
                <span className="ml-0.5 text-[11px] font-normal text-ink-3">
                  {meridiem(session.startTime)}
                </span>
              </p>

              {session.endTime && (
                /*
                 * The end time, and am/pm on it only when it changes.
                 *
                 * Nearly every class here starts and ends in the same half of
                 * the day, so repeating it put "pm" twice on every card in an
                 * eleven-card column — noise in the one place the eye is meant
                 * to travel fastest. It comes back for 11:45am–12:15pm, which is
                 * exactly when it carries information.
                 */
                <p className="text-[12.5px] leading-tight text-ink-3">
                  {clockTime(session.endTime)}
                  {meridiem(session.endTime) !== meridiem(session.startTime) &&
                    meridiem(session.endTime)}
                </p>
              )}
            </>
          ) : (
            // No start time means it meets on no date (D-84) — say so rather
            // than leaving a blank column somebody reads as midnight.
            <p className="text-[12px] leading-tight text-ink-3">No time</p>
          )}
        </div>

        {/*
          `min-w-[11rem]` is what drops the button to its own row on a phone.

          With the button on the same line, a 390px card left this column about
          ninety pixels wide — "Caitlin Hineborg" broke across two lines and the
          class name ran to five. A minimum width the line cannot honour makes
          the flex container wrap instead of squeeze, which is the behaviour
          wanted: full-width text, button underneath.
        */}
        <div className="min-w-[11rem] flex-1">
          {marker && (
            <p
              className={`mb-1 text-[11px] font-bold tracking-[0.11em] uppercase ${
                marker === 'now' ? 'text-accent' : 'text-ink-3'
              }`}
            >
              {marker === 'now' ? 'On now' : 'Next'}
            </p>
          )}

          {/*
            **The one she came back for** (D-914). Badged in place rather than
            sorted to the top: she is scanning for this, while a teacher after
            *the 5:30* is scanning for the time.
          */}
          {notTaken && (
            <p className="mb-1 inline-block rounded-md bg-warn-bg px-1.5 py-0.5 text-[11px] font-bold tracking-[0.08em] text-warn uppercase">
              Not taken
            </p>
          )}

          {/* Whose class it is, promoted out of the grey line. "Is this mine?"
              is the question that decides whether she reads any further. */}
          {session.instructors.length > 0 && (
            <p className="text-[15px] font-semibold wrap-anywhere">
              {session.instructors.join(', ')}
            </p>
          )}

          {/*
            The class name, demoted but never truncated (CLAUDE.md).

            It stays because name + weekday + start time is what identifies a
            class (D-88), and the distinguishing part of a Jackrabbit string is
            at the *end* — clipping it removes the answer to "which one is this".
          */}
          <p className="mt-0.5 text-[13px] leading-snug text-ink-2 wrap-anywhere">
            {session.className}
          </p>

          <p className="mt-1.5 text-[12.5px] text-ink-3 tabular">
            {held ? (
              // Not "0 of 0 marked", which reads as a job not yet done.
              <span>Room held &mdash; no dancers yet</span>
            ) : (
              <span>
                {session.marked} of {session.enrolled} marked
              </span>
            )}
            {showRoom && session.roomName && (
              <span className="text-ink-3"> &middot; {session.roomName}</span>
            )}
          </p>
        </div>

        <div className="ml-auto flex shrink-0 items-center gap-2">
          {session.musicUrl && (
            // No sign-in. A dancer running her routine in here during a break
            // needs the music, not an account (D-119).
            <a
              href={session.musicUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Music for ${session.className}`}
              className="grid size-11 place-items-center rounded-xl border border-line-2 text-ink-2 no-underline"
            >
              <Music size={17} strokeWidth={2} />
            </a>
          )}

          <button
            type="button"
            onClick={onTake}
            className={`rounded-xl px-4 py-2.5 text-[14px] font-semibold ${
              complete || held
                ? 'border border-line-2 bg-surface text-ink-2'
                : 'bg-accent text-on-accent'
            }`}
          >
            {complete ? 'Done' : held ? 'Open' : 'Take attendance'}
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * First run: somebody sets the tablet up once.
 *
 * Deliberately plain and deliberately not hidden — a device that will not
 * register is a device somebody has to fix standing in a studio corridor.
 */
function Register({ onDone }: { onDone: () => void }) {
  const [token, setToken] = useState('')

  return (
    <div className="grid flex-1 place-items-center px-6">
      <div className="w-full max-w-[24rem] text-center">
        {/*
          "Device", not "tablet" (D-128). A teacher's own phone is a device
          here, and while the studio has not bought tablets yet it is the only
          one — copy that assumes hardware nobody owns is copy that reads as
          broken.
        */}
        <h1 className="font-display text-2xl">Set up this device</h1>
        <p className="mt-2 text-[14px] text-ink-3">
          Enter the setup code from the studio. You only do this once, and it works the same on a
          phone as on a tablet.
        </p>

        <input
          value={token}
          onChange={(e) => setToken(e.target.value)}
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          aria-label="Setup code"
          className="mt-5 w-full rounded-xl border border-line-2 bg-surface px-4 py-3 text-center text-[16px] outline-none focus:border-accent"
        />

        <button
          type="button"
          disabled={token.trim().length === 0}
          onClick={() => {
            setDeviceToken(token)
            onDone()
          }}
          className="mt-3 w-full rounded-xl bg-accent px-5 py-3 text-[15px] font-semibold text-on-accent disabled:opacity-40"
        >
          Set up
        </button>

        {deviceToken() && (
          <p className="mt-4 text-[12.5px] text-crit">
            This device has a code saved, but the studio did not recognise it. It may have been
            retired &mdash; ask for a new one.
          </p>
        )}
      </div>
    </div>
  )
}

/**
 * Pick a name, tap four digits.
 *
 * A numeric pad rather than a text field: a password keyboard on a tablet
 * between two classes is the friction the whole design removes (D-116).
 */
function PinPad({
  className,
  prompt = 'Who is taking this class?',
  teachers,
  onCancel,
  onUnlocked,
}: {
  className: string
  /**
   * What the PIN is being asked for.
   *
   * Defaulted rather than required, because the register is what this pad has
   * always been for — but finding a dancer is not taking a class, and a pad
   * headed "Who is taking this class?" over a dancer search is asking somebody
   * to answer a question that has nothing to do with what they pressed (D-249).
   */
  prompt?: string
  teachers: { staffId: string; name: string }[]
  onCancel: () => void
  onUnlocked: () => void
}) {
  const [staffId, setStaffId] = useState<string | null>(teachers.length === 1 ? teachers[0].staffId : null)
  const [pin, setPin] = useState('')

  const attempt = useMutation({
    mutationFn: (value: string) => unlock(staffId!, value),
    onSuccess: onUnlocked,
    onError: () => setPin(''),
  })

  // Submits itself at four digits. Asking for a confirming tap after the last
  // one is a step with no decision in it.
  useEffect(() => {
    if (pin.length === 4 && staffId && !attempt.isPending) attempt.mutate(pin)
  }, [pin, staffId, attempt])

  if (!staffId) {
    return (
      <div className="mx-auto w-full max-w-[34rem] px-5 py-6">
        <Back onClick={onCancel} />

        <h1 className="mt-4 font-display text-xl">{prompt}</h1>
        {className && (
          <p className="mt-1 text-[13.5px] text-ink-3 wrap-anywhere">{className}</p>
        )}

        {teachers.length === 0 ? (
          <p className="mt-5 rounded-2xl border border-line bg-surface px-5 py-6 text-center text-[14px] text-ink-3">
            Nobody has a PIN set up yet. Ask the studio to add one.
          </p>
        ) : (
          <div className="mt-4 flex flex-col gap-2.5">
            {teachers.map((teacher) => (
              <button
                key={teacher.staffId}
                type="button"
                onClick={() => setStaffId(teacher.staffId)}
                className="rounded-2xl border border-line bg-surface px-5 py-4 text-left text-[16px] font-semibold"
              >
                {teacher.name}
              </button>
            ))}
          </div>
        )}
      </div>
    )
  }

  const who = teachers.find((t) => t.staffId === staffId)?.name ?? ''

  return (
    <div className="mx-auto w-full max-w-[22rem] px-5 py-6">
      <Back onClick={onCancel} />

      <h1 className="mt-4 text-center font-display text-xl">{who}</h1>
      <p className="mt-1 text-center text-[13px] text-ink-3">Enter your PIN</p>

      {/*
        What this unlocks.
        Shown in the picker already, and missing here — so a studio with one
        PIN-holding teacher skips straight to a pad that names nobody's class.
        That is fine walking in from the list, and not fine after the tablet
        locked itself mid-register (D-234): she comes back to a PIN prompt with
        no indication of what she was in the middle of.

        The class name when there is one, and otherwise what was pressed — a
        studio with a single teacher went straight to a bare keypad after
        tapping *Find a dancer*, which is the same defect with a different
        cause (D-249).
      */}
      <p className="mt-2 text-center text-[12.5px] text-ink-3 wrap-anywhere">
        {className || prompt}
      </p>

      <div className="mt-5 flex justify-center gap-3" aria-live="polite">
        {[0, 1, 2, 3].map((i) => (
          <span
            key={i}
            className={`size-3.5 rounded-full ${i < pin.length ? 'bg-accent' : 'bg-surface-2 border border-line-2'}`}
          />
        ))}
      </div>

      {attempt.isError && (
        <p role="alert" className="mt-3 text-center text-[13px] text-crit">
          {(attempt.error as Error).message}
        </p>
      )}

      <div className="mt-6 grid grid-cols-3 gap-3">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
          <PinKey key={digit} onClick={() => setPin((p) => (p + digit).slice(0, 4))}>
            {digit}
          </PinKey>
        ))}

        <span />
        <PinKey onClick={() => setPin((p) => (p + '0').slice(0, 4))}>0</PinKey>
        <PinKey onClick={() => setPin((p) => p.slice(0, -1))} label="Delete">
          <Delete size={20} strokeWidth={2} />
        </PinKey>
      </div>
    </div>
  )
}

function PinKey({
  children,
  onClick,
  label,
}: {
  children: React.ReactNode
  onClick: () => void
  label?: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="grid h-16 place-items-center rounded-2xl border border-line bg-surface text-[22px] font-semibold active:bg-surface-2"
    >
      {children}
    </button>
  )
}

function Back({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex items-center gap-1.5 text-[14px] font-semibold text-ink-3"
    >
      <ArrowLeft size={16} strokeWidth={2} />
      Back
    </button>
  )
}

/**
 * The register.
 *
 * **Every tap saves.** There is no submit button, because a teacher who walks
 * away from a half-filled screen must not lose what she already marked — and
 * one who forgets to press Save has, from the studio's point of view, not taken
 * the register at all.
 */
function Roster({
  occurrenceId,
  onClose,
  onSessionEnded,
}: {
  occurrenceId: string
  onClose: () => void
  /** The kiosk session died mid-register: send her to the PIN, not to nothing. */
  onSessionEnded: () => void
}) {
  const queryClient = useQueryClient()

  const { data: sheet, isPending, isError, error, refetch } = useQuery({
    queryKey: ['sheet', occurrenceId],
    queryFn: () => fetchSheet(occurrenceId),
    retry: false,
  })

  /** A tap that did not save, said out loud rather than only rolled back. */
  const [markProblem, setMarkProblem] = useState<string | null>(null)

  /*
   * <b>Taking a visitor back off the night</b> (D-806).
   *
   * <b>Not optimistic, unlike marking below.</b> A mark must colour under her
   * thumb because studio wifi is slow and she is not waiting; removing
   * somebody is a decision she has just confirmed, and a row that vanished and
   * came back would be worse than one that takes a moment to go.
   */
  const takeOff = useMutation({
    mutationFn: (dancerId: string) => removeMakeupVisitor(occurrenceId, dancerId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['sheet', occurrenceId] })
      void queryClient.invalidateQueries({ queryKey: ['absences'] })
    },
  })

  const mark = useMutation({
    mutationFn: ({ dancerId, state, note, familyNote }: { dancerId: string; state: AttendanceState; note?: string; familyNote?: string }) =>
      markAttendance(occurrenceId, [{ dancerId, state, note, familyNote }]),

    // Optimistic: the button must colour under her thumb, not after a round
    // trip. Studio Wi-Fi is not fast and she is not waiting.
    onMutate: async ({ dancerId, state }) => {
      await queryClient.cancelQueries({ queryKey: ['sheet', occurrenceId] })
      const previous = queryClient.getQueryData(['sheet', occurrenceId])

      queryClient.setQueryData(['sheet', occurrenceId], (old: typeof sheet) =>
        old
          ? {
              ...old,
              enrolled: old.enrolled.map((d) => (d.dancerId === dancerId ? { ...d, state } : d)),
              makeupVisitors: old.makeupVisitors.map((d) =>
                d.dancerId === dancerId ? { ...d, state } : d,
              ),
            }
          : old,
      )

      return { previous }
    },

    /*
     * A rollback is not an answer on its own (scenario 9, D-186's family).
     *
     * The optimistic update colours the row instantly, so a failure that only
     * rolls back is a green flash the teacher has already looked away from. She
     * finishes the register believing it saved. It did not, and nothing said so
     * — on the one screen whose data Jackrabbit cannot regenerate.
     *
     * A dead session is the case that actually happens, because the tablet locks
     * itself after fifteen idle minutes (D-234) and the roster stays on screen
     * while it does. So: roll back — leaving the row visibly *unmarked*, which is
     * the truth — and send her to the PIN pad for this class. After a correct PIN
     * she is back on the roster with that tap still to do, where she can see it.
     */
    onError: (error, _v, context) => {
      queryClient.setQueryData(['sheet', occurrenceId], context?.previous)

      const message = (error as Error).message

      // A dead session is the one a PIN fixes, so it is the only one that goes
      // to the pad. Everything else says so on the spot: rolling back without a
      // word is the silent failure this whole branch exists to end.
      if (message === SESSION_ENDED) {
        onSessionEnded()
        return
      }

      setMarkProblem(
        message === NOT_YOURS
          ? 'That mark was refused — this class is not open on this tablet today.'
          : 'That did not save. Check the studio system is reachable and tap again.',
      )
    },

    onSuccess: () => setMarkProblem(null),

    onSettled: () => queryClient.invalidateQueries({ queryKey: ['sheet', occurrenceId] }),
  })

  const counts = useMemo(() => (sheet ? progress(sheet) : null), [sheet])

  /*
   * A roster that cannot be loaded says so — scenario 10, and a regression that
   * existed before this slice.
   *
   * This branch did not exist: any failure left the screen on "Loading the
   * register…" forever. The first 75 seconds after a deploy are exactly that
   * (D-33), and so is a kiosk session that has since expired — and a spinner
   * that never resolves is the silent-failure family D-209 forbids. A teacher
   * standing in front of eleven dancers needs to be told, and given something to
   * press.
   */
  if (isError) {
    /*
     * Three refusals, three sentences — and the difference is not cosmetic.
     *
     * These were two: anything 401 or 403 said "this tablet has locked itself".
     * Watching a teacher open a colleague's register on a day that was not
     * today showed what that costs — the session was alive, the message said it
     * was not, and the remedy it offered (enter your PIN) fails in exactly the
     * same way. She would have concluded the tablet was broken. A refusal must
     * never render as a statement about something else (D-209).
     */
    const message = (error as Error).message
    const expired = message === SESSION_ENDED
    const notYours = message === NOT_YOURS

    const title = expired
      ? 'This tablet has locked itself'
      : notYours
        ? 'That class is not open here'
        : 'The attendance sheet could not be loaded'

    const detail = expired
      ? 'It has been a while since you last took attendance. Go back and enter your PIN again.'
      : notYours
        ? 'A tablet can open any of today’s classes, and your own classes on other days. This one is somebody else’s, on a day that is not today.'
        : 'The studio system did not answer. This is normal for a minute after an update.'

    return (
      <div className="mx-auto w-full max-w-[40rem] px-5 py-6">
        <Back onClick={onClose} />

        <div className="mt-6 rounded-2xl border border-crit bg-crit-bg px-5 py-6 text-center">
          <AlertCircle size={22} strokeWidth={2} className="mx-auto text-crit" />

          <p className="mt-3 text-[15px] font-semibold">{title}</p>

          <p className="mt-1.5 text-[13.5px] leading-relaxed text-ink-2">{detail}</p>

          <div className="mt-4 flex justify-center gap-2.5">
            {/* No "Try again" for either refusal: retrying a 401 or a 403 fails
                identically, and a button that cannot work is worse than none. */}
            {!expired && !notYours && (
              <button
                type="button"
                onClick={() => refetch()}
                className="rounded-xl bg-accent px-5 py-3 text-[15px] font-semibold text-on-accent"
              >
                Try again
              </button>
            )}

            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-line-2 px-5 py-3 text-[15px] font-semibold text-ink-2"
            >
              Back to the classes
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (isPending || !sheet) return <Splash>Loading attendance&hellip;</Splash>

  return (
    <div className="mx-auto w-full max-w-[40rem] px-5 py-6">
      <Back onClick={onClose} />

      <header className="mt-4 mb-4">
        <h1 className="font-display text-xl wrap-anywhere">{sheet.className}</h1>

        <p className="mt-1 flex flex-wrap items-center gap-x-2.5 text-[13px] text-ink-3">
          {sheet.startTime && (
            <span className="tabular">
              {clockTime(sheet.startTime)}
              {meridiem(sheet.startTime)}
            </span>
          )}
          {sheet.roomName && <span>{sheet.roomName}</span>}
          {counts && (
            <span className="tabular">
              {counts.marked} of {counts.total} marked
            </span>
          )}
        </p>

        {sheet.isCancelled && (
          <p className="mt-2 rounded-xl border border-warn bg-warn-bg px-4 py-2.5 text-[13px]">
            This session was cancelled &mdash; it does not count toward anyone&rsquo;s attendance.
          </p>
        )}

        {/* role="alert" so it is announced, not only coloured — a tap that did
            not save is exactly the thing a sighted user might also miss. */}
        {markProblem && (
          <p
            role="alert"
            className="mt-2 rounded-xl border border-crit bg-crit-bg px-4 py-2.5 text-[13px] text-crit"
          >
            {markProblem}
          </p>
        )}
      </header>

      {sheet.enrolled.length === 0 && sheet.makeupVisitors.length === 0 && (
        <p className="rounded-2xl border border-line bg-surface px-5 py-8 text-center text-[14px] text-ink-3">
          Nobody is enrolled in this class yet.
        </p>
      )}

      <div className="flex flex-col gap-2.5">
        {sheet.enrolled.map((dancer) => (
          <DancerRow key={dancer.dancerId} dancer={dancer} onMark={mark.mutate} />
        ))}
      </div>

      {/* Never merged with the roster — a teacher needs to know which dancers
          are hers (PRD §7.6). */}
      {sheet.makeupVisitors.length > 0 && (
        <>
          <h2 className="mt-7 mb-2 text-[12px] font-bold tracking-[0.11em] text-ink-3 uppercase">
            Making up
          </h2>

          <div className="flex flex-col gap-2.5">
            {sheet.makeupVisitors.map((dancer) => (
              <DancerRow
                key={dancer.dancerId}
                dancer={dancer}
                onMark={mark.mutate}
                onRemove={() => takeOff.mutate(dancer.dancerId)}
              />
            ))}
          </div>
        </>
      )}

      {/*
        A dancer who walks in to make up a missed class. Behind a button rather
        than always open, because it is the exception — but reachable in one tap,
        because it happens while she is standing there waiting to be let in.
      */}
      <AddVisitor occurrenceId={occurrenceId} />

      {counts?.done && (
        <p className="mt-6 flex items-center justify-center gap-2 text-[14px] font-semibold text-good">
          <Check size={17} strokeWidth={2.5} />
          Everyone marked
        </p>
      )}
    </div>
  )
}

function AddVisitor({ occurrenceId }: { occurrenceId: string }) {
  const queryClient = useQueryClient()

  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [picked, setPicked] = useState<{ dancerId: string; name: string } | null>(null)

  /*
   * The kiosk lookup, not the studio search (D-233).
   *
   * `searchDancers` answers with age, household name and household id on a
   * substring match — a slice of the family directory, returned to a screen on a
   * wall. This one answers with a name and where she should be, which is the
   * whole of what somebody at the door needs, and it is what the owner asked
   * for: "type a dancer name, show which room they should be in, that's it".
   */
  const { data: hits } = useQuery({
    queryKey: ['kiosk-dancer-lookup', query],
    queryFn: () => lookupDancer(query),
    enabled: open && query.trim().length >= 2,
  })

  // What she is here to make up. Offered, never demanded — the link can be
  // attached afterwards, and refusing her at the door would send her home.
  // Keyed on the night too (D-795): the list is specific to what this room
  // teaches, so the dancer alone would serve one night's answer on another.
  const { data: absences } = useQuery({
    queryKey: ['absences', picked?.dancerId, occurrenceId],
    queryFn: () => fetchUnrepairedAbsences(picked!.dancerId, occurrenceId),
    enabled: picked !== null,
  })

  const add = useMutation({
    mutationFn: (missedOccurrenceId: string | null) =>
      addMakeupVisitor(occurrenceId, picked!.dancerId, missedOccurrenceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sheet', occurrenceId] })
      setOpen(false)
      setPicked(null)
      setQuery('')
    },
  })

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-5 flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-line-2 py-4 text-[14px] font-semibold text-ink-3"
      >
        <UserPlus size={16} strokeWidth={2} />
        Someone making up
      </button>
    )
  }

  return (
    <section className="mt-5 rounded-2xl border border-accent bg-accent-soft px-4 py-4">
      {!picked ? (
        <>
          <div className="flex items-center gap-2.5 rounded-xl border border-line-2 bg-surface px-3.5 py-2.5 focus-within:border-accent">
            <Search size={17} strokeWidth={2} className="shrink-0 text-ink-3" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
              placeholder="Dancer's name"
              aria-label="Search for a dancer"
              className="min-w-0 flex-1 bg-transparent text-[15px] outline-none"
            />
          </div>

          {query.trim().length >= 2 && hits?.length === 0 && (
            <p className="mt-3 text-center text-[13px] text-ink-3">Nobody by that name.</p>
          )}

          <div className="mt-3 flex flex-col gap-2">
            {hits?.slice(0, 6).map((hit) => (
              <button
                key={hit.dancerId}
                type="button"
                onClick={() => setPicked({ dancerId: hit.dancerId, name: hit.name })}
                className="rounded-xl border border-line bg-surface px-4 py-3 text-left"
              >
                <span className="block text-[15px] font-semibold">{hit.name}</span>

                {/*
                  Where she should be — the answer to the question actually being
                  asked at the door (D-233). Says so plainly when the answer is
                  "nowhere today", which is the case for most dancers most nights
                  and is exactly what somebody making up looks like.
                */}
                <span className="mt-0.5 block text-[12.5px] text-ink-3 wrap-anywhere">
                  {hit.today.length === 0
                    ? 'No class today'
                    : hit.today
                        .map((p) =>
                          [
                            p.startTime ? `${clockTime(p.startTime)}${meridiem(p.startTime)}` : null,
                            p.roomName,
                            p.className,
                          ]
                            .filter(Boolean)
                            .join(' · '),
                        )
                        .join('  |  ')}
                </span>
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          <p className="text-[15px] font-semibold">{picked.name}</p>
          <p className="mt-0.5 text-[13px] text-ink-2">Which class are they making up?</p>

          <div className="mt-3 flex flex-col gap-2">
            {absences?.map((absence) => (
              <button
                key={absence.occurrenceId}
                type="button"
                disabled={add.isPending}
                onClick={() => add.mutate(absence.occurrenceId)}
                className="rounded-xl border border-line bg-surface px-4 py-3 text-left text-[14px] wrap-anywhere"
              >
                {absence.label}
              </button>
            ))}

            {absences?.length === 0 && (
              <p className="text-[13px] text-ink-3">
                Nothing on record that they have missed &mdash; add them anyway and the studio can
                sort it out.
              </p>
            )}

            <button
              type="button"
              disabled={add.isPending}
              onClick={() => add.mutate(null)}
              className="rounded-xl bg-accent px-4 py-3 text-[14px] font-semibold text-on-accent"
            >
              {absences && absences.length > 0 ? 'Not sure — add them anyway' : 'Add them'}
            </button>
          </div>
        </>
      )}

      {add.isError && (
        <p role="alert" className="mt-2.5 text-[13px] text-crit">
          {(add.error as Error).message}
        </p>
      )}

      <button
        type="button"
        onClick={() => {
          setOpen(false)
          setPicked(null)
          setQuery('')
        }}
        className="mt-3 w-full py-2 text-[13px] font-semibold text-ink-3"
      >
        Cancel
      </button>
    </section>
  )
}

function DancerRow({
  dancer,
  onMark,
  onRemove,
}: {
  dancer: SheetDancer
  onMark: (mark: { dancerId: string; state: AttendanceState; note?: string; familyNote?: string }) => void
  /** Takes a visiting dancer back off this night (D-806). */
  onRemove?: () => void
}) {
  /*
   * The same lock as the teacher's own register (D-188, D-639). A class
   * half-marked on the tablet and finished on her phone is one register, and it
   * must not have two sets of rules.
   *
   * <b>Absent is not part of the lock, on either surface.</b> The lock exists
   * to stop a dancer whose family reported her out being recorded as having
   * turned up when she did not; marking her absent is that report being
   * confirmed, and asking first is a question with one sensible answer. It only
   * became reachable when both surfaces grew past two states.
   */
  const [unlocked, setUnlocked] = useState(false)
  const locked = needsOverride(dancer) && !unlocked

  const summary = reportedSummary(dancer)

  return (
    <div
      className={`rounded-2xl border px-4 py-3 ${
        locked ? 'border-warn bg-warn-bg' : 'border-line bg-surface'
      }`}
    >
      <p className="text-[16px] font-semibold wrap-anywhere">{dancer.name}</p>

      {dancer.makesUpFor && (
        // So a teacher knows why an unfamiliar dancer is standing in her room.
        <p className="mt-0.5 text-[12.5px] text-ink-3">Making up {dancer.makesUpFor}</p>
      )}

      {/*
        <b>The undo, on the screen where the wrong name gets tapped</b>
        (D-806). The wall tablet is a shared device in a corridor and this is
        where a drop-in is added in a hurry.

        <b>Keyed on the role, never on `makesUpFor`</b>: a visitor added
        without naming an absence is the ordinary banked case (D-610) and has
        no sentence above, so keying on that would offer the undo for some
        visitors and not others. The server refuses an enrolled dancer either
        way.
      */}
      {dancer.role === 'MakeupVisitor' && onRemove && (
        <TakeOffTheNight name={dancer.name} onRemove={onRemove} />
      )}

      {summary && (
        <div className="mt-1.5 flex flex-col gap-0.5">
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[13px] font-semibold text-warn">
            <AlertCircle size={14} strokeWidth={2.5} className="shrink-0" />
            {summary}
          </p>

          {dancer.reportedNote && (
            <p className="text-[12.5px] wrap-anywhere text-ink-2">
              &ldquo;{dancer.reportedNote}&rdquo;
            </p>
          )}

          {/*
            The fact only. This screen is a wall-mounted tablet in a room full
            of children — the last place a medical note should be openable, and
            D-9 keeps the file behind an owner-only capability the kiosk
            ceiling does not include.
          */}
          {dancer.hasDocument && (
            <p className="text-[12.5px] text-ink-3">Note on file</p>
          )}
        </div>
      )}

      {locked ? (
        <div className="mt-2.5 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => setUnlocked(true)}
            className="rounded-xl border border-warn bg-surface py-3 text-[14px] font-semibold text-warn"
          >
            {dancer.name.split(' ')[0]} is here after all?
          </button>

          {/*
            Confirming the family's report, without first claiming she turned up
            (D-639). The tablet used to offer only the unlock, so a teacher
            agreeing that a dancer was away had to press *is here after all?* to
            reach the button that said she was not - which is the opposite of
            what she meant, on the surface with the least room to explain itself.
          */}
          <button
            type="button"
            aria-label={`Mark ${dancer.name} Absent`}
            onClick={() => onMark({ dancerId: dancer.dancerId, state: 'Unexcused' })}
            className="rounded-xl border border-line-2 bg-surface py-3 text-[14px] font-semibold text-ink-3"
          >
            Absent
          </button>
        </div>
      ) : (
        /*
          Two across, two down (D-128, D-634). It was three across for three
          buttons: at 320px a four-column row gives each about 70px, under the
          comfortable tap target, and this is the control the whole feature
          exists for. Watched made them four, so they wrap instead of
          narrowing.
        */
        <MarkButtons
          dancerName={dancer.name}
          state={dancer.state}
          note={dancer.note}
          familyNote={dancer.familyNote}
          columns={2}
          onMark={(state, note, familyNote) =>
            onMark({ dancerId: dancer.dancerId, state, note, familyNote })
          }
        />
      )}
    </div>
  )
}

/**
 * "Where is this dancer meant to be?", behind the PIN (D-249).
 *
 * <b>The question a teacher at the door actually has.</b> A parent arrives with
 * a child and nobody knows which room; today that is a walk to the office. The
 * endpoint has existed since D-233 and answered from exactly one place — the
 * makeup-visitor search inside a roster — so the studio could not ask it without
 * first opening a register for a class.
 *
 * <b>Name, room, time. Nothing else, and the omissions are the design.</b> No
 * date of birth, no age, no household, no guardian, no contact number. This runs
 * on a wall in a public corridor, and a directory of minors is what D-119
 * refused; the PIN in front of it is the other half of that refusal.
 */
export function FindDancer({ onClose }: { onClose: () => void }) {
  const [query, setQuery] = useState('')

  const {
    data: hits,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['kiosk-dancer-lookup', query],
    queryFn: () => lookupDancer(query),
    // The server ignores anything shorter, and asking anyway spends a round
    // trip per keystroke to be told so.
    enabled: query.trim().length >= 2,
    retry: false,

    /*
     * Keep the last answer on screen while the next one loads.
     *
     * The query is part of the key, so without this every keystroke wipes the
     * results and repaints them when the response lands — a seven-letter name
     * flashes the answer away five times, at a tablet, in front of a parent.
     * The same argument as the day arrows above (D-235), reached by measuring
     * rather than reading.
     */
    placeholderData: keepPreviousData,
  })

  const searching = query.trim().length >= 2
  const expired = isError && error?.message === SESSION_ENDED

  return (
    <div className="mx-auto w-full max-w-[34rem] px-5 py-6">
      <Back onClick={onClose} />

      <h1 className="mt-4 font-display text-xl">Find a dancer</h1>
      <p className="mt-1 text-[13.5px] text-ink-3">
        Which room they are in today. Nothing else.
      </p>

      <input
        autoFocus
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Dancer&rsquo;s name"
        aria-label="Dancer's name"
        className="mt-4 w-full rounded-xl border border-line-2 bg-surface px-4 py-3 text-[16px] outline-none focus:border-accent"
      />

      {!searching && (
        <p className="mt-4 text-[13.5px] text-ink-3">Type at least two letters.</p>
      )}

      {/*
        A failed lookup used to render as nothing at all — `hits` undefined, so
        neither the empty card nor the hint showed, and the panel was
        indistinguishable from still loading with no way to retry. Two ordinary
        cases reach it: the ~75 seconds of refusal after a deploy (D-33) and the
        idle expiry (D-234). Silence is the one answer a screen must not give.
      */}
      {expired && (
        <div className="mt-4 rounded-2xl border border-warn bg-warn-bg px-5 py-6 text-center">
          <p className="text-[14px]">The tablet locked itself. Enter a PIN again to search.</p>
          <button
            type="button"
            onClick={onClose}
            className="mt-3 rounded-xl bg-accent px-4 py-2.5 text-[14px] font-semibold text-on-accent"
          >
            Back to the day
          </button>
        </div>
      )}

      {isError && !expired && (
        <div className="mt-4 rounded-2xl border border-warn bg-warn-bg px-5 py-6 text-center">
          <p className="text-[14px]">Could not reach the studio. Nothing is lost &mdash; try again.</p>
          <button
            type="button"
            onClick={() => refetch()}
            className="mt-3 rounded-xl border border-line-2 px-4 py-2.5 text-[14px] font-semibold text-ink-2"
          >
            Try again
          </button>
        </div>
      )}

      {searching && !isError && hits?.length === 0 && !isFetching && (
        // Never "not enrolled" — this searches the whole studio, so the honest
        // answer is about the search, not about the child.
        <p className="mt-4 rounded-2xl border border-line bg-surface px-5 py-6 text-center text-[14px] text-ink-3">
          Nobody by that name. Try a surname, or part of one.
        </p>
      )}

      <div className="mt-4 flex flex-col gap-2.5">
        {hits?.map((hit) => (
          <div key={hit.dancerId} className="rounded-2xl border border-line bg-surface px-4 py-3.5">
            <p className="text-[16px] font-semibold wrap-anywhere">{hit.name}</p>

            {hit.today.length === 0 ? (
              // She exists and has nothing on. Worth saying plainly: it is the
              // answer to "should she be here at all?", which is the other
              // reason somebody is standing at the door.
              <p className="mt-1 text-[13.5px] text-ink-3">No class today.</p>
            ) : (
              <ul className="mt-2 flex flex-col gap-1.5">
                {hit.today.map((placement) => (
                  <li
                    key={`${placement.className}-${placement.startTime}`}
                    className="flex flex-wrap items-baseline gap-x-2 text-[14px]"
                  >
                    <span className="font-semibold text-accent-2">
                      {placement.roomName ?? 'Room not set'}
                    </span>
                    {placement.startTime && (
                      <span className="text-ink-2">
                        {clockTime(placement.startTime)}
                        {meridiem(placement.startTime)}
                      </span>
                    )}
                    <span className="min-w-0 text-[13px] text-ink-3 wrap-anywhere">
                      {placement.className}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

/**
 * Every routine's music, for a dancer in an empty room (D-119).
 */
function MusicBrowser({ onClose }: { onClose: () => void }) {
  const [query, setQuery] = useState('')

  const { data, isPending } = useQuery({ queryKey: ['device-music'], queryFn: fetchMusicFolders })

  const matches = useMemo(() => filterMusic(data ?? [], query), [data, query])

  return (
    <div className="mx-auto w-full max-w-[40rem] px-5 py-6">
      <Back onClick={onClose} />

      <h1 className="mt-4 font-display text-xl">Music</h1>

      <div className="mt-4 flex items-center gap-2.5 rounded-xl border border-line-2 bg-surface px-3.5 py-2.5 focus-within:border-accent">
        <Search size={17} strokeWidth={2} className="shrink-0 text-ink-3" />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search for a routine"
          aria-label="Search for a routine"
          className="min-w-0 flex-1 bg-transparent text-[15px] outline-none"
        />
      </div>

      {isPending && <p className="mt-5 text-[14px] text-ink-3">Loading&hellip;</p>}

      {data && matches.length === 0 && (
        <p className="mt-5 rounded-2xl border border-line bg-surface px-5 py-8 text-center text-[14px] text-ink-3">
          {query ? `Nothing matching "${query}".` : 'No music folders have been added yet.'}
        </p>
      )}

      <div className="mt-4 flex flex-col gap-2.5">
        {matches.map((folder) => (
          <a
            key={folder.classId}
            href={folder.musicUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-3 rounded-2xl border border-line bg-surface px-4 py-3.5 no-underline"
          >
            <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-accent-soft text-accent-2">
              <Music size={18} strokeWidth={2} />
            </span>

            <span className="min-w-0 flex-1">
              <span className="block text-[15.5px] font-semibold wrap-anywhere">
                {routineLabel(folder)}
              </span>

              {folder.routineName && (
                <span className="block text-[12.5px] text-ink-3 wrap-anywhere">
                  {folder.className}
                </span>
              )}
            </span>
          </a>
        ))}
      </div>
    </div>
  )
}
